import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Switch,
  useMediaQuery,
  useTheme,
  Chip,
  IconButton,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  Notifications as NotificationsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  Visibility as VisibilityIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import axios from 'axios';
import { alpha } from '@mui/material/styles';
import { GlassmorphicCard } from '../styles/sharedStyles';

const formatDateInWarsaw = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'N/A';
    // timeZone: 'Europe/Warsaw' applies CET (UTC+1) in winter and CEST (UTC+2) in summer automatically.
    return date.toLocaleString('pl-PL', {
      timeZone: 'Europe/Warsaw',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).replace(',', '');
  } catch (error) {
    console.error('Error formatting Warsaw time:', error);
    return dateString;
  }
};

const UserManagement = ({ apiBaseUrl, token, userInfo }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isRoot = userInfo?.is_admin === true;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    password_confirm: '',
    email: '',
  });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState({
    username: '',
    password: '',
    password_confirm: '',
    email: '',
  });
  const [editSaving, setEditSaving] = useState(false);
  const [tabIds, setTabIds] = useState({ tab_ids: [], labels: {} });
  const [tabsDialogOpen, setTabsDialogOpen] = useState(false);
  const [tabsDialogUser, setTabsDialogUser] = useState(null);
  const [tabsDialogSelected, setTabsDialogSelected] = useState([]);
const [expandedGroups, setExpandedGroups] = useState({});

  const [tabsDialogSaving, setTabsDialogSaving] = useState(false);
  const [notifCategories, setNotifCategories] = useState({ categories: [], labels: {} });
  const [notifDialogOpen, setNotifDialogOpen] = useState(false);
  const [notifDialogUser, setNotifDialogUser] = useState(null);
  const [notifDialogSelected, setNotifDialogSelected] = useState([]);
  const [notifDialogSaving, setNotifDialogSaving] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [activityDialogUser, setActivityDialogUser] = useState(null);
  const [activityData, setActivityData] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  const [wolToggling, setWolToggling] = useState(false);

  // Fetch users on mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Fetch tab-ids when root (for Edit tabs dialog)
  useEffect(() => {
    if (!isRoot || !apiBaseUrl || !token) return;
    axios
      .get(`${apiBaseUrl}/api/tab-ids`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setTabIds(res.data))
      .catch(() => setTabIds({ tab_ids: [], labels: {} }));

    axios
      .get(`${apiBaseUrl}/api/notification-categories`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setNotifCategories(res.data))
      .catch(() => setNotifCategories({ categories: [], labels: {} }));
  }, [isRoot, apiBaseUrl, token]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiBaseUrl}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data.users || []);
      setErrorMessage('');
    } catch (err) {
      console.error('Error fetching users:', err);
      setErrorMessage('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserActivity = async (username) => {
    try {
      setActivityLoading(true);
      const response = await axios.get(`${apiBaseUrl}/api/users/${encodeURIComponent(username)}/activity`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActivityData(response.data.activities || []);
      setErrorMessage('');
    } catch (err) {
      console.error('Error fetching user activity:', err);
      setErrorMessage('Failed to load activity history');
      setActivityData([]);
    } finally {
      setActivityLoading(false);
    }
  };

  const handleAddUserClick = () => {
    setNewUser({ username: '', password: '', password_confirm: '', email: '' });
    setOpenDialog(true);
  };

  const handleEditUserClick = (user) => {
    setEditUser({
      username: user.username,
      password: '',
      password_confirm: '',
      email: user.email || '',
    });
    setEditDialogOpen(true);
  };

  const handleEditUserClose = () => {
    setEditDialogOpen(false);
  };

  const handleEditUserChange = (e) => {
    const { name, value } = e.target;
    setEditUser(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditUserSave = async () => {
    if (editUser.password && editUser.password !== editUser.password_confirm) {
      setErrorMessage('Passwords do not match');
      return;
    }

    if (editUser.password && editUser.password.length < 6) {
      setErrorMessage('Password must be at least 6 characters');
      return;
    }

    try {
      setEditSaving(true);
      setErrorMessage('');
      setSuccessMessage('');

      const response = await axios.put(
        `${apiBaseUrl}/api/users/${encodeURIComponent(editUser.username)}`,
        {
          password: editUser.password || undefined,
          email: editUser.email,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setSuccessMessage(`✅ User '${editUser.username}' updated successfully`);
        setEditDialogOpen(false);
        await fetchUsers();
      } else {
        setErrorMessage(response.data.message || 'Failed to update user');
      }
    } catch (err) {
      console.error('Error updating user:', err);
      setErrorMessage(err.response?.data?.message || 'Error updating user');
    } finally {
      setEditSaving(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleNewUserChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddUser = async () => {
    if (!newUser.username || !newUser.password) {
      setErrorMessage('Username and password are required');
      return;
    }

    if (newUser.password !== newUser.password_confirm) {
      setErrorMessage('Passwords do not match');
      return;
    }

    if (newUser.password.length < 6) {
      setErrorMessage('Password must be at least 6 characters');
      return;
    }

    try {
      setSaving(true);
      setErrorMessage('');
      setSuccessMessage('');

      const response = await axios.post(
        `${apiBaseUrl}/api/users`,
        {
          username: newUser.username,
          password: newUser.password,
          email: newUser.email,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setSuccessMessage(`✅ User '${newUser.username}' created successfully`);
        setOpenDialog(false);
        await fetchUsers();
      } else {
        setErrorMessage(response.data.message || 'Failed to create user');
      }
    } catch (err) {
      console.error('Error adding user:', err);
      setErrorMessage(err.response?.data?.message || 'Error creating user');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Are you sure you want to delete user '${username}'?`)) {
      return;
    }

    try {
      setSaving(true);
      const response = await axios.delete(
        `${apiBaseUrl}/api/users/${username}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setSuccessMessage(`✅ User '${username}' deleted successfully`);
        await fetchUsers();
      } else {
        setErrorMessage(response.data.message || 'Failed to delete user');
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      setErrorMessage(err.response?.data?.message || 'Error deleting user');
    } finally {
      setSaving(false);
    }
  };

  const handleEditTabsClick = (user) => {
    setTabsDialogUser(user);
    setTabsDialogSelected(user.allowed_tabs ?? tabIds.tab_ids ?? []);
    setTabsDialogOpen(true);
  };

  const handleTabsDialogClose = () => {
    setTabsDialogOpen(false);
    setTabsDialogUser(null);
  };

  const handleTabsDialogToggle = (tabId) => {
    setTabsDialogSelected((prev) =>
      prev.includes(tabId) ? prev.filter((t) => t !== tabId) : [...prev, tabId]
    );
  };

  const handleTabsDialogSelectAll = () => {
    if (tabsDialogSelected.length === (tabIds.tab_ids || []).length) {
      setTabsDialogSelected([]);
    } else {
      setTabsDialogSelected([...(tabIds.tab_ids || [])]);
    }
  };

  // Organize tabs into groups matching App.js MAIN_TAB_CONFIG structure
  const tabGroupConfig = {
    dashboard: { label: 'Dashboard', color: '#00d4ff', tabs: [] },
    immich: { label: 'Immich', color: '#ff6b6b', tabs: [] },
    jellyfin: { label: 'Jellyfin', color: '#32c75a', tabs: [] },
    nas_drive: { label: 'NAS Drive', color: '#9c27b0', tabs: [] },
    servers: { label: 'Servers', color: '#ff9800', tabs: [] },
    home_assistant: { label: 'Home Assistant', color: '#4caf50', tabs: [] },
    unifi: { label: 'UniFi', color: '#007bff', tabs: [] },
    settings: { label: 'Settings', color: '#ffab40', tabs: [] },
    polish_tracker: { label: 'Polish Tracker', color: '#ff6b6b', tabs: [] },
    oralb: { label: 'Oral-B', color: '#00bca0', tabs: [] },
    tax: { label: 'Tax', color: '#ffeb3b', tabs: [] },
    nginx: { label: 'Nginx', color: '#009638', tabs: [] },
  };

  const tabPrimaryOrder = {
    dashboard: ['dashboard'],
    immich: ['immich_statistics', 'immich_adam_library', 'immich_jobs', 'immich_users', 'immich_libraries', 'immich_nightly_tasks', 'immich_about'],
    jellyfin: ['jellyfin'],
    nas_drive: ['nas_drive', 'nas_drive_piotrek', 'nas_drive_adam'],
    servers: ['truenas_services', 'truenas_apps', 'truenas_cron_jobs', 'truenas_virtual_machines', 'truenas_storage_pools', 'truenas_data_protection', 'truenas_file_explorer', 'services_truenas', 'services_rpi5', 'debian_sysmon', 'services_ha', 'task_scheduler', 'docker_maintenance', 'docker_security', 'nfs_mounts'],
    home_assistant: ['home_assistant', 'home_assistant_automations', 'home_assistant_room_designer', 'home_assistant_backups'],
    unifi: ['unifi_overview', 'unifi_ports', 'unifi_top_clients', 'unifi_wifi'],
    settings: [
      // Maintenance
      'maintenance_logs', 'gui_theme', 'logon_page', 'maintenance_changelog', 'roadmap',
      // Security (docker_security moved to Servers)
      // Bonus - Chat
      'chat',
      // Bonus - Games
      'game_snake', 'game_server_ping', 'game_disk_whack', 'game_docker_stack',
    ],
    polish_tracker: ['polish_tracker'],
    oralb: ['oralb'],
    tax: ['tax'],
    nginx: ['nginx_proxy_manager'],
  };

  const getTabGroups = () => {
    const groups = {};
    Object.keys(tabGroupConfig).forEach(k => {
      groups[k] = { label: tabGroupConfig[k].label, color: tabGroupConfig[k].color, tabs: [] };
    });

     (tabIds.tab_ids || []).forEach(tabId => {
      const label = tabIds.labels?.[tabId] ?? tabId;
      if (tabId === 'dashboard') {
        groups.dashboard.tabs.push({ id: tabId, label });
      } else if (tabId === 'oralb') {
        groups.oralb.tabs.push({ id: tabId, label });
      } else if (tabId.startsWith('immich_')) {
        groups.immich.tabs.push({ id: tabId, label });
      } else if (tabId === 'jellyfin') {
        groups.jellyfin.tabs.push({ id: tabId, label });
      } else if (tabId === 'polish_tracker') {
        groups.polish_tracker.tabs.push({ id: tabId, label });
      } else if (tabId.startsWith('nas_drive')) {
        groups.nas_drive.tabs.push({ id: tabId, label });
      } else if (tabId === 'tax') {
        groups.tax.tabs.push({ id: tabId, label });
      } else if (tabId === 'home_assistant' || tabId.startsWith('home_assistant_')) {
        groups.home_assistant.tabs.push({ id: tabId, label });
      } else if (tabId === 'nginx_proxy_manager') {
        groups.nginx.tabs.push({ id: tabId, label });
      } else if (tabId.startsWith('unifi_')) {
        groups.unifi.tabs.push({ id: tabId, label });
      } else if (tabId.startsWith('truenas_') || tabId.startsWith('services_') || tabId === 'debian_sysmon' || tabId === 'task_scheduler' || tabId === 'docker_maintenance' || tabId === 'docker_security' || tabId === 'nfs_mounts') {
        groups.servers.tabs.push({ id: tabId, label });
      } else if (
        tabId === 'chat' ||
        tabId === 'maintenance_changelog' ||
        tabId === 'maintenance_logs' ||
        tabId === 'gui_theme' ||
        tabId === 'logon_page' ||
        tabId === 'roadmap' ||
        tabId.startsWith('game_')
      ) {
        groups.settings.tabs.push({ id: tabId, label });
      }
     });

    return Object.entries(groups)
      .filter(([_, g]) => g.tabs.length > 0)
      .map(([key, g]) => ({
        key,
        label: g.label,
        color: g.color,
        tabs: g.tabs.sort((a, b) => {
          const order = tabPrimaryOrder[key] || [];
          const idxA = order.indexOf(a.id);
          const idxB = order.indexOf(b.id);
          if (idxA >= 0 && idxB >= 0) return idxA - idxB;
          if (idxA >= 0) return -1;
          if (idxB >= 0) return 1;
          return a.label.localeCompare(b.label);
        }),
      }));
  };

  const tabGroups = useMemo(() => getTabGroups(), [tabIds]);

  const handleToggleGroup = (groupKey) => {
    setExpandedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const handleGroupSelectAll = (groupKey) => {
    const group = tabGroups.find((g) => g.key === groupKey);
    if (!group) return;
    const allSelected = group.tabs.every((t) => tabsDialogSelected.includes(t.id));
    if (allSelected) {
      setTabsDialogSelected((prev) => prev.filter((t) => !group.tabs.some((gt) => gt.id === t)));
    } else {
      setTabsDialogSelected((prev) => [...new Set([...prev, ...group.tabs.map((t) => t.id)])]);
    }
  };

  const handleTabsDialogSave = async () => {
    if (!tabsDialogUser) return;
    try {
      setTabsDialogSaving(true);
      const response = await axios.put(
        `${apiBaseUrl}/api/users/${encodeURIComponent(tabsDialogUser.username)}/tabs`,
        { allowed_tabs: tabsDialogSelected },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setSuccessMessage(`✅ Tab access for '${tabsDialogUser.username}' updated`);
        handleTabsDialogClose();
        await fetchUsers();
      } else {
        setErrorMessage(response.data.message || 'Failed to update tab access');
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.detail || err.response?.data?.message || 'Failed to update tab access');
    } finally {
      setTabsDialogSaving(false);
    }
  };

  const handleEditNotificationsClick = (user) => {
    setNotifDialogUser(user);
    setNotifDialogSelected(user.allowed_notifications ?? notifCategories.categories ?? []);
    setNotifDialogOpen(true);
  };

  const handleNotifDialogClose = () => {
    setNotifDialogOpen(false);
    setNotifDialogUser(null);
  };

  const handleNotifDialogToggle = (catId) => {
    setNotifDialogSelected((prev) =>
      prev.includes(catId) ? prev.filter((c) => c !== catId) : [...prev, catId]
    );
  };

  const handleNotifDialogSelectAll = () => {
    if (notifDialogSelected.length === (notifCategories.categories || []).length) {
      setNotifDialogSelected([]);
    } else {
      setNotifDialogSelected([...(notifCategories.categories || [])]);
    }
  };

  const handleNotifDialogSave = async () => {
    if (!notifDialogUser) return;
    try {
      setNotifDialogSaving(true);
      const response = await axios.put(
        `${apiBaseUrl}/api/users/${encodeURIComponent(notifDialogUser.username)}/notifications`,
        { allowed_notifications: notifDialogSelected },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setSuccessMessage(`✅ Notification settings for '${notifDialogUser.username}' updated`);
        handleNotifDialogClose();
        await fetchUsers();
      } else {
        setErrorMessage(response.data.message || 'Failed to update notification settings');
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.detail || err.response?.data?.message || 'Failed to update notification settings');
    } finally {
      setNotifDialogSaving(false);
    }
  };

  

  const handleWolToggle = async (username, currentEnabled) => {
    const newEnabled = !currentEnabled;
    try {
      setWolToggling(true);
      setErrorMessage('');
      setSuccessMessage('');
      const response = await axios.put(
        `${apiBaseUrl}/api/users/${encodeURIComponent(username)}/wol-enabled`,
        { wol_enabled: newEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setUsers(prevUsers =>
          prevUsers.map(u =>
            u.username === username ? { ...u, wol_enabled: newEnabled } : u
          )
        );
        setSuccessMessage(`✅ WOL ${newEnabled ? 'enabled' : 'disabled'} for '${username}'`);
      } else {
        setErrorMessage(response.data.message || 'Failed to update WOL permission');
      }
    } catch (err) {
      console.error('Error updating WOL permission:', err);
      setErrorMessage(err.response?.data?.detail || err.response?.data?.message || 'Failed to update WOL permission');
    } finally {
      setWolToggling(false);
    }
  };

  const handleActivityClick = (user) => {
    setActivityDialogUser(user);
    fetchUserActivity(user.username);
    setActivityDialogOpen(true);
  };

  const handleActivityDialogClose = () => {
    setActivityDialogOpen(false);
    setActivityDialogUser(null);
    setActivityData([]);
  };

  const sharedActionButtonSx = (color) => ({
    color: color,
    border: `1px solid ${alpha(color, 0.3)}`,
    px: 1.5,
    py: 0.5,
    fontSize: '0.8rem',
    fontWeight: 500,
    textTransform: 'none',
    borderRadius: '8px',
    '&:hover': { backgroundColor: alpha(color, 0.1) },
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: '#00d4ff' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', mx: 'auto' }}>
      {/* Messages */}
      {successMessage && (
        <Alert
          severity="success"
          onClose={() => setSuccessMessage('')}
          sx={{ mb: 2, borderRadius: '12px' }}
        >
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert
          severity="error"
          onClose={() => setErrorMessage('')}
          sx={{ mb: 2, borderRadius: '12px' }}
        >
          {errorMessage}
        </Alert>
      )}

      <GlassmorphicCard
        sx={{
          borderRadius: '16px',
          width: '100%',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                color: '#00d4ff',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              👥 User Management
            </Typography>
            <Button
              variant="contained"
              onClick={handleAddUserClick}
              disabled={saving}
              sx={{
                backgroundColor: (theme) => theme.palette.secondary.main,
                color: (theme) => theme.palette.secondary.contrastText,
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '8px',
                px: 2,
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.secondary.main,
                  filter: 'brightness(1.1)',
                  boxShadow: (theme) => `0 0 20px ${theme.palette.secondary.main}66`,
                },
              }}
            >
              <AddIcon sx={{ mr: 1 }} />
              Add User
            </Button>
          </Box>

          <Typography
            variant="body2"
            sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6 }}
          >
            Manage login users for the WatchTower application. Create new users or remove existing ones.
          </Typography>

          <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          {/* Users - Card layout for mobile, Table for desktop */}
          {users.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {users.map((user) => (
                <GlassmorphicCard
                  key={user.username}
                  sx={{
                    p: 0,
                    width: '100%',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 20px 40px rgba(0, 212, 255, 0.08)',
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? 2 : 0,
                      }}
                    >
                      {/* User Info */}
                      <Box sx={{ flex: 1, width: isMobile ? '100%' : 'auto', minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <PersonIcon sx={{ color: user.is_admin ? '#00d4ff' : 'rgba(255,255,255,0.6)', fontSize: isMobile ? 22 : 20 }} />
                          <Typography
                            sx={{
                              color: '#fff',
                              fontWeight: 600,
                              fontSize: isMobile ? '1.15rem' : '1rem',
                            }}
                            title={user.username}
                          >
                            {user.username}
                          </Typography>
                          {user.is_admin && (
                            <Chip
                              label="Root"
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(0, 212, 255, 0.2)',
                                color: '#00d4ff',
                                fontSize: '0.7rem',
                                height: 22,
                              }}
                            />
                          )}
                        </Box>
                        <Typography
                          sx={{
                            color: 'rgba(255, 255, 255, 0.55)',
                            fontSize: '0.85rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={user.email || ''}
                        >
                          {user.email || 'No email set'}
                        </Typography>
                      </Box>

                      {/* Action Buttons */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: isMobile ? 'wrap' : 'nowrap',
                          justifyContent: isMobile ? 'flex-start' : 'flex-end',
                          width: isMobile ? '100%' : 'auto',
                        }}
                      >
                        {isRoot && (
                          <Tooltip title="Edit user">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleEditUserClick(user)}
                                disabled={saving}
                                sx={{
                                  color: '#00d4ff',
                                  border: '1px solid rgba(0, 212, 255, 0.3)',
                                  borderRadius: '8px',
                                  width: 36,
                                  height: 36,
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 212, 255, 0.1)',
                                  },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {isRoot && !user.is_admin && (
                          <>
                            <Button
                              size="small"
                              onClick={() => handleEditTabsClick(user)}
                              disabled={saving}
                              sx={sharedActionButtonSx('#ffab40')}
                            >
                              <VisibilityIcon fontSize="small" sx={{ mr: 0.5 }} />
                              Tabs
                            </Button>
                            <Tooltip title="Notification settings">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditNotificationsClick(user)}
                                  disabled={saving}
                                  sx={{
                                    color: '#b388ff',
                                    border: '1px solid rgba(179, 136, 255, 0.3)',
                                    borderRadius: '8px',
                                    width: 36,
                                    height: 36,
                                    '&:hover': {
                                      backgroundColor: 'rgba(179, 136, 255, 0.1)',
                                    },
                                  }}
                                >
                                  <NotificationsIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Wake-on-LAN permission">
                              <span>
                                <Switch
                                  checked={user.wol_enabled !== false}
                                  onChange={() => handleWolToggle(user.username, user.wol_enabled !== false)}
                                  disabled={wolToggling}
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                      color: '#00d4ff',
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
                                      backgroundColor: '#00d4ff',
                                    },
                              '& .MuiSwitch-switchBase': {
                                      '&:hover': {
                                        backgroundColor: 'rgba(0, 212, 255, 0.2)',
                                      },
                                    },
                                    mr: isMobile ? 0 : -1,
                                  }}
                                />
                              </span>
                            </Tooltip>
                          </>
                        )}
                        {(isRoot || user.username === userInfo?.username) && (
                          <Button
                            size="small"
                            onClick={() => handleActivityClick(user)}
                            disabled={saving}
                            sx={sharedActionButtonSx('#00d4ff')}
                          >
                            <HistoryIcon fontSize="small" sx={{ mr: 0.5 }} />
                            Activity
                          </Button>
                        )}
                        {isRoot && (
                          <Tooltip title="Delete user">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteUser(user.username)}
                                disabled={saving || users.length === 1}
                                sx={{
                                  color: '#ff6b6b',
                                  border: '1px solid rgba(255, 107, 107, 0.3)',
                                  borderRadius: '8px',
                                  width: 36,
                                  height: 36,
                                  '&:hover': {
                                    backgroundColor: 'rgba(255, 107, 107, 0.1)',
                                  },
                                  '&:disabled': {
                                    color: 'rgba(255, 107, 107, 0.5)',
                                    borderColor: 'rgba(255, 107, 107, 0.2)',
                                  },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </GlassmorphicCard>
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                textAlign: 'center',
                py: 6,
                color: 'rgba(255, 255, 255, 0.7)',
                background: 'rgba(255, 255, 255, 0.02)',
                borderRadius: '12px',
                border: '1px dashed rgba(255, 255, 255, 0.1)',
              }}
            >
              <PersonIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                No users found
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                Create a new user above to get started
              </Typography>
            </Box>
          )}
        </CardContent>
      </GlassmorphicCard>

      {/* Add User Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, rgba(22, 24, 35, 0.96), rgba(28, 30, 44, 0.96))',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: isMobile ? 0 : '16px',
            boxShadow: '0 0 60px rgba(0,0,0,0.6)',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: '#00d4ff',
            fontWeight: 600,
            pb: 1,
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            background: 'rgba(0, 212, 255, 0.03)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PersonAddIcon sx={{ color: '#00d4ff', fontSize: 22 }} />
            <span>Add New User</span>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: isMobile ? 1 : 2, pb: 2 }}>
          <TextField
            fullWidth
            label="Username"
            name="username"
            value={newUser.username}
            onChange={handleNewUserChange}
            margin="normal"
            placeholder="Enter username"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&.Mui-focused fieldset': { borderColor: '#00d4ff' },
              },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
            }}
          />
          <TextField
            fullWidth
            label="Email for 2FA"
            name="email"
            value={newUser.email}
            onChange={handleNewUserChange}
            margin="normal"
            placeholder="e.g. user@example.com"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&.Mui-focused fieldset': { borderColor: '#00d4ff' },
              },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
            }}
          />
          <TextField
            fullWidth
            label="Password"
            name="password"
            type="password"
            value={newUser.password}
            onChange={handleNewUserChange}
            margin="normal"
            placeholder="Enter password (min 6 chars)"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&.Mui-focused fieldset': { borderColor: '#00d4ff' },
              },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
            }}
          />
          <TextField
            fullWidth
            label="Confirm Password"
            name="password_confirm"
            type="password"
            value={newUser.password_confirm}
            onChange={handleNewUserChange}
            margin="normal"
            placeholder="Re-enter password"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&.Mui-focused fieldset': { borderColor: '#00d4ff' },
              },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddUser}
            disabled={saving}
            variant="contained"
            sx={{
              backgroundColor: (theme) => theme.palette.secondary.main,
              color: (theme) => theme.palette.secondary.contrastText,
              fontWeight: 600,
              '&:hover': {
                backgroundColor: (theme) => theme.palette.secondary.main,
                filter: 'brightness(1.1)'
              }
            }}
          >
            {saving ? <CircularProgress size={20} sx={{ mr: 1 }} /> : <CheckIcon sx={{ mr: 1 }} />}
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditUserClose}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, rgba(30, 30, 40, 0.95), rgba(40, 40, 50, 0.95))',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: '#00d4ff',
            fontWeight: 600,
            pb: 1,
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            background: 'rgba(0, 212, 255, 0.03)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <EditIcon sx={{ color: '#00d4ff', fontSize: 22 }} />
            <span>Edit User: {editUser.username}</span>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: isMobile ? 1 : 2, pb: 2 }}>
          <TextField
            fullWidth
            label="Email for 2FA"
            name="email"
            value={editUser.email}
            onChange={handleEditUserChange}
            margin="normal"
            placeholder="e.g. user@example.com"
            helperText="Separate multiple emails with commas"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&.Mui-focused fieldset': { borderColor: '#00d4ff' },
              },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.5)' },
            }}
          />
          <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', mb: 1, display: 'block' }}>
            Leave password fields empty to keep current password.
          </Typography>
          <TextField
            fullWidth
            label="New Password"
            name="password"
            type="password"
            value={editUser.password}
            onChange={handleEditUserChange}
            margin="normal"
            placeholder="Enter new password"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&.Mui-focused fieldset': { borderColor: '#00d4ff' },
              },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
            }}
          />
          <TextField
            fullWidth
            label="Confirm New Password"
            name="password_confirm"
            type="password"
            value={editUser.password_confirm}
            onChange={handleEditUserChange}
            margin="normal"
            placeholder="Re-enter new password"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&.Mui-focused fieldset': { borderColor: '#00d4ff' },
              },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleEditUserClose}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditUserSave}
            disabled={editSaving}
            variant="contained"
            sx={{
              backgroundColor: (theme) => theme.palette.secondary.main,
              color: (theme) => theme.palette.secondary.contrastText,
              fontWeight: 600,
              '&:hover': {
                backgroundColor: (theme) => theme.palette.secondary.main,
                filter: 'brightness(1.1)'
              }
            }}
          >
            {editSaving ? <CircularProgress size={20} sx={{ mr: 1 }} /> : <CheckIcon sx={{ mr: 1 }} />}
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit user tabs dialog (root only) - styled to match Activity History */}
      <Dialog
        open={tabsDialogOpen}
        onClose={handleTabsDialogClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, rgba(22, 24, 35, 0.96), rgba(28, 30, 44, 0.96))',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: isMobile ? 0 : '16px',
            boxShadow: '0 0 60px rgba(0,0,0,0.6)',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: '#00d4ff',
            fontWeight: 600,
            pb: 1,
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            background: 'rgba(0, 212, 255, 0.03)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <TuneIcon sx={{ color: '#00d4ff', fontSize: 22 }} />
            <span>Tab Access — {tabsDialogUser?.username}</span>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: isMobile ? 1 : 2, pb: 2 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', mb: 2, lineHeight: 1.6 }}>
            Select which tabs this user can see. Leave all unchecked to allow all tabs (default).
          </Typography>

          {/* Summary Chips */}
          <Box sx={{ display: 'flex', gap: isMobile ? 0.75 : 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
            <Chip
              label={`${tabsDialogSelected.length} selected`}
              size="small"
              sx={{
                background: 'rgba(0, 212, 255, 0.1)',
                color: '#00d4ff',
                border: '1px solid rgba(0, 212, 255, 0.3)',
                fontWeight: 600,
              }}
            />
            <Chip
              label={`${tabIds.tab_ids?.length ?? 0} total`}
              size="small"
              sx={{
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'rgba(255, 255, 255, 0.6)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                fontWeight: 500,
              }}
            />
          </Box>

          {/* Select All / Deselect All */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              size="small"
              onClick={handleTabsDialogSelectAll}
              sx={{
                color: '#00d4ff',
                fontSize: '0.8rem',
                textTransform: 'none',
                '&:hover': { backgroundColor: 'rgba(0, 212, 255, 0.08)' },
              }}
            >
              {tabsDialogSelected.length === (tabIds.tab_ids || []).length
                ? 'Deselect all'
                : tabsDialogSelected.length > 0
                  ? 'Select all'
                  : 'Select all'}
            </Button>
          </Box>

          {/* Organized tab groups with collapsible sections */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {tabGroups.map((group) => {
              const allSelected = group.tabs.every((t) => tabsDialogSelected.includes(t.id));
              const someSelected = group.tabs.some((t) => tabsDialogSelected.includes(t.id));
              const isExpanded = expandedGroups[group.key] !== false;

              return (
                <GlassmorphicCard
                  key={group.key}
                  sx={{
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    p: 1.5,
                    '&:hover': {
                      border: `1px solid ${alpha(group.color, 0.4)}`,
                      background: 'rgba(255, 255, 255, 0.03)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                    onClick={() => handleToggleGroup(group.key)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: group.color,
                          boxShadow: `0 0 8px ${alpha(group.color, 0.6)}`,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: group.color,
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          fontSize: '0.75rem',
                          letterSpacing: '1px',
                        }}
                      >
                        {group.label}
                      </Typography>
                      <Chip
                        label={`${group.tabs.filter((t) => tabsDialogSelected.includes(t.id)).length}/${group.tabs.length}`}
                        size="small"
                        sx={{
                          background: alpha(group.color, 0.1),
                          color: group.color,
                          border: `1px solid ${alpha(group.color, 0.3)}`,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          height: 20,
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGroupSelectAll(group.key);
                        }}
                        sx={{
                          color: someSelected ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.5)',
                          fontSize: '0.7rem',
                          minWidth: 'auto',
                          px: 1,
                          '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                        }}
                      >
                        {allSelected ? 'Clear' : someSelected ? 'Select All' : 'Select All'}
                      </Button>
                      {isExpanded ? (
                        <ExpandLessIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 18 }} />
                      ) : (
                        <ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 18 }} />
                      )}
                    </Box>
                  </Box>
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <FormGroup>
                      {group.tabs.map((tab) => (
                        <FormControlLabel
                          key={tab.id}
                          control={
                            <Checkbox
                              checked={tabsDialogSelected.includes(tab.id)}
                              onChange={() => handleTabsDialogToggle(tab.id)}
                              sx={{
                                color: alpha(group.color, 0.5),
                                '&.Mui-checked': { color: group.color },
                              }}
                            />
                          }
                          label={tab.label}
                          sx={{
                            color: 'rgba(255,255,255,0.85)',
                            '& .MuiFormControlLabel-label': { fontSize: '0.85rem' },
                          }}
                        />
                      ))}
                    </FormGroup>
                  </Collapse>
                </GlassmorphicCard>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            borderTop: '1px solid rgba(255, 255, 255, 0.06)',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Button
            onClick={handleTabsDialogClose}
            variant="outlined"
            size="small"
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '10px',
              textTransform: 'none',
              '&:hover': {
                background: 'rgba(0, 212, 255, 0.08)',
                borderColor: 'rgba(0, 212, 255, 0.3)',
                color: '#00d4ff',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleTabsDialogSave}
            disabled={tabsDialogSaving}
            variant="contained"
            sx={{
              backgroundColor: (theme) => theme.palette.secondary.main,
              color: (theme) => theme.palette.secondary.contrastText,
              fontWeight: 600,
              borderRadius: '10px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: (theme) => theme.palette.secondary.main,
                filter: 'brightness(1.1)',
                boxShadow: (theme) => `0 0 20px ${theme.palette.secondary.main}66`,
              },
            }}
          >
            {tabsDialogSaving ? <CircularProgress size={20} sx={{ mr: 1 }} /> : <CheckIcon sx={{ mr: 1 }} />}
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit user notifications dialog (root only) - styled to match Activity History */}
      <Dialog
        open={notifDialogOpen}
        onClose={handleNotifDialogClose}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, rgba(22, 24, 35, 0.96), rgba(28, 30, 44, 0.96))',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: isMobile ? 0 : '16px',
            boxShadow: '0 0 60px rgba(0,0,0,0.6)',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: '#00d4ff',
            fontWeight: 600,
            pb: 1,
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            background: 'rgba(0, 212, 255, 0.03)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <NotificationsIcon sx={{ color: '#00d4ff', fontSize: 22 }} />
            <span>Notifications — {notifDialogUser?.username}</span>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: isMobile ? 1 : 2 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', mb: 2, lineHeight: 1.6 }}>
            Select which notification categories this user should receive.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              size="small"
              onClick={handleNotifDialogSelectAll}
              sx={{
                color: '#00d4ff',
                fontSize: '0.8rem',
                textTransform: 'none',
                '&:hover': { backgroundColor: 'rgba(0, 212, 255, 0.08)' },
              }}
            >
              {notifDialogSelected.length === (notifCategories.categories || []).length
                ? 'Deselect all'
                : notifDialogSelected.length > 0
                  ? 'Select all'
                  : 'Select all'}
            </Button>
          </Box>
          <FormGroup>
            {(notifCategories.categories || []).map((catId) => (
              <FormControlLabel
                key={catId}
                control={
                  <Checkbox
                    checked={notifDialogSelected.includes(catId)}
                    onChange={() => handleNotifDialogToggle(catId)}
                    sx={{
                      color: 'rgba(255,255,255,0.5)',
                      '&.Mui-checked': { color: '#b388ff' },
                    }}
                  />
                }
                label={notifCategories.labels?.[catId] ?? catId}
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  mb: 0.5,
                  '& .MuiFormControlLabel-label': { fontSize: '0.85rem' },
                }}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            borderTop: '1px solid rgba(255, 255, 255, 0.06)',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Button
            onClick={handleNotifDialogClose}
            variant="outlined"
            size="small"
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '10px',
              textTransform: 'none',
              '&:hover': {
                background: 'rgba(0, 212, 255, 0.08)',
                borderColor: 'rgba(0, 212, 255, 0.3)',
                color: '#00d4ff',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleNotifDialogSave}
            disabled={notifDialogSaving}
            variant="contained"
            sx={{
              backgroundColor: '#b388ff',
              color: '#fff',
              fontWeight: 600,
              borderRadius: '10px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#9575cd',
                boxShadow: '0 0 20px rgba(179, 136, 255, 0.4)',
              },
            }}
          >
            {notifDialogSaving ? <CircularProgress size={20} sx={{ mr: 1 }} /> : <CheckIcon sx={{ mr: 1 }} />}
            Save
          </Button>
        </DialogActions>
      </Dialog>

{/* Activity Dialog */}
      <Dialog
        open={activityDialogOpen}
        onClose={handleActivityDialogClose}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, rgba(22, 24, 35, 0.96), rgba(28, 30, 44, 0.96))',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: isMobile ? 0 : '16px',
            boxShadow: '0 0 60px rgba(0,0,0,0.6)',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: '#00d4ff',
            fontWeight: 600,
            pb: 1,
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            background: 'rgba(0, 212, 255, 0.03)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <HistoryIcon sx={{ color: '#00d4ff', fontSize: 22 }} />
            <span>Activity History — {activityDialogUser?.username}</span>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: isMobile ? 1 : 2, minWidth: isMobile ? 'auto' : 600 }}>
          {activityLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress sx={{ color: '#00d4ff' }} />
            </Box>
          ) : activityData.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 6,
                color: 'rgba(255, 255, 255, 0.6)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <HistoryIcon sx={{ fontSize: 48, color: 'rgba(255, 255, 255, 0.2)' }} />
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                No activity history found
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                This user has no recorded login attempts yet.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1 : 2 }}>
              {/* Summary chips */}
              <Box sx={{ display: 'flex', gap: isMobile ? 0.75 : 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', mr: 'auto', fontSize: isMobile ? '0.7rem' : '0.85rem' }}>
                  Showing recent {activityData.length} {activityData.length === 1 ? 'event' : 'events'}
                </Typography>
                <Chip
                  label={`Total: ${activityData.length}`}
                  size="small"
                  sx={{
                    background: 'rgba(0, 212, 255, 0.1)',
                    color: '#00d4ff',
                    border: '1px solid rgba(0, 212, 255, 0.3)',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label={`Success: ${activityData.filter((a) => a.status === 'success').length}`}
                  size="small"
                  sx={{
                    background: alpha('#00ff88', 0.12),
                    color: '#00ff88',
                    border: '1px solid rgba(0, 255, 136, 0.3)',
                    fontWeight: 600,
                    boxShadow: '0 0 8px rgba(0, 255, 136, 0.25)',
                  }}
                />
                <Chip
                  label={`Failed: ${activityData.filter((a) => a.status !== 'success').length}`}
                  size="small"
                  sx={{
                    background: alpha('#ff4444', 0.12),
                    color: '#ff4444',
                    border: '1px solid rgba(255, 68, 68, 0.3)',
                    fontWeight: 600,
                    boxShadow: '0 0 8px rgba(255, 68, 68, 0.25)',
                  }}
                />
              </Box>

              {isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {activityData.map((activity) => {
                    const isSuccess = activity.status === 'success';
                    const statusColor = isSuccess ? '#00ff88' : '#ff4444';
                    return (
                      <Box
                        key={activity.id}
                        sx={{
                          p: 1.25,
                          borderRadius: '10px',
                          border: '1px solid rgba(255, 255, 255, 0.06)',
                          borderLeft: `3px solid ${statusColor}`,
                          background: isSuccess ? alpha('#00ff88', 0.04) : alpha('#ff4444', 0.04),
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            background: isSuccess ? alpha('#00ff88', 0.08) : alpha('#ff4444', 0.08),
                            boxShadow: `inset 0 0 16px ${isSuccess ? alpha('#00ff88', 0.15) : alpha('#ff4444', 0.15)}`,
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Chip
                            label={isSuccess ? 'Success' : 'Failed'}
                            size="small"
                            sx={{
                              backgroundColor: isSuccess
                                ? alpha('#00ff88', 0.15)
                                : alpha('#ff4444', 0.15),
                              color: statusColor,
                              border: `1px solid ${isSuccess ? alpha('#00ff88', 0.3) : alpha('#ff4444', 0.3)}`,
                              fontWeight: 600,
                              fontSize: '0.68rem',
                              height: 20,
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ color: 'rgba(255, 255, 255, 0.7)', fontFamily: 'monospace', fontSize: '0.72rem' }}
                          >
                            {formatDateInWarsaw(activity.created_at)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.35 }}>
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.7rem' }}>
                            IP:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: 'rgba(255, 255, 255, 0.85)', fontFamily: 'monospace', fontSize: '0.8rem' }}
                          >
                            {activity.ip_address || 'Unknown'}
                          </Typography>
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            display: 'block',
                            width: '100%',
                            color: 'rgba(255, 255, 255, 0.5)',
                            fontSize: '0.68rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={activity.user_agent || 'Unknown'}
                        >
                          {activity.user_agent || 'Unknown'}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <TableContainer
                  sx={{
                    maxHeight: 500,
                    borderRadius: '12px',
                    overflow: 'hidden',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    background: 'rgba(0, 0, 0, 0.15)',
                    backdropFilter: 'blur(6px)',
                  }}
                >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          color: '#00d4ff',
                          fontWeight: 700,
                          background: 'rgba(0, 212, 255, 0.06)',
                          borderBottom: '1px solid rgba(0, 212, 255, 0.2)',
                          fontSize: '0.8rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Date & Time
                      </TableCell>
                      <TableCell
                        sx={{
                          color: '#00d4ff',
                          fontWeight: 700,
                          background: 'rgba(0, 212, 255, 0.06)',
                          borderBottom: '1px solid rgba(0, 212, 255, 0.2)',
                          fontSize: '0.8rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Status
                      </TableCell>
                      <TableCell
                        sx={{
                          color: '#00d4ff',
                          fontWeight: 700,
                          background: 'rgba(0, 212, 255, 0.06)',
                          borderBottom: '1px solid rgba(0, 212, 255, 0.2)',
                          fontSize: '0.8rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        IP Address
                      </TableCell>
                      <TableCell
                        sx={{
                          color: '#00d4ff',
                          fontWeight: 700,
                          background: 'rgba(0, 212, 255, 0.06)',
                          borderBottom: '1px solid rgba(0, 212, 255, 0.2)',
                          fontSize: '0.8rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Device
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activityData.map((activity) => {
                      const isSuccess = activity.status === 'success';
                      const statusColor = isSuccess ? '#00ff88' : '#ff4444';
                      return (
                        <TableRow
                          key={activity.id}
                          hover
                          sx={{
                            borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
                            backgroundColor: isSuccess
                              ? alpha('#00ff88', 0.02)
                              : alpha('#ff4444', 0.02),
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: isSuccess
                                ? alpha('#00ff88', 0.06)
                                : alpha('#ff4444', 0.06),
                              boxShadow: `inset 0 0 20px ${isSuccess ? alpha('#00ff88', 0.15) : alpha('#ff4444', 0.15)}`,
                            },
                          }}
                        >
                          <TableCell
                            sx={{
                              color: 'rgba(255, 255, 255, 0.9)',
                              whiteSpace: 'nowrap',
                              fontFamily: 'monospace',
                              fontSize: '0.85rem',
                            }}
                          >
                            {formatDateInWarsaw(activity.created_at)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={isSuccess ? 'Success' : 'Failed'}
                              size="small"
                              sx={{
                                backgroundColor: isSuccess
                                  ? alpha('#00ff88', 0.15)
                                  : alpha('#ff4444', 0.15),
                                color: statusColor,
                                fontWeight: 600,
                                border: `1px solid ${isSuccess ? alpha('#00ff88', 0.3) : alpha('#ff4444', 0.3)}`,
                                boxShadow: `0 0 8px ${isSuccess ? alpha('#00ff88', 0.3) : alpha('#ff4444', 0.3)}`,
                                '& .MuiChip-label': { px: 1 },
                                textTransform: 'uppercase',
                                fontSize: '0.72rem',
                                letterSpacing: '0.5px',
                              }}
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              color: 'rgba(255, 255, 255, 0.8)',
                              fontFamily: 'monospace',
                              fontSize: '0.85rem',
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-block',
                                px: 1.2,
                                py: 0.4,
                                borderRadius: '6px',
                                background: 'rgba(255, 255, 255, 0.05)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                fontFamily: 'monospace',
                              }}
                            >
                              {activity.ip_address || 'Unknown'}
                            </Box>
                          </TableCell>
                          <TableCell
                            sx={{
                              color: 'rgba(255, 255, 255, 0.6)',
                              width: 280,
                              maxWidth: 280,
                              padding: '8px 8px',
                            }}
                          >
                            <Tooltip title={activity.user_agent || 'Unknown device'}>
                              <Box
                                component="span"
                                sx={{
                                  display: 'block',
                                  width: '100%',
                                  maxWidth: 280,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  color: 'rgba(255, 255, 255, 0.65)',
                                  fontFamily: 'monospace',
                                  fontSize: '0.8rem',
                                  cursor: 'default',
                                }}
                              >
                                {activity.user_agent || 'Unknown'}
                              </Box>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.06)' }}>
          <Button
            onClick={handleActivityDialogClose}
            variant="outlined"
            size="small"
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '10px',
              textTransform: 'none',
              '&:hover': {
                background: 'rgba(0, 212, 255, 0.08)',
                borderColor: 'rgba(0, 212, 255, 0.3)',
                color: '#00d4ff',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
